export type SessionTerminalReason =
  | 'completed'
  | 'expired'
  | 'unauthorized'
  | 'not_found'
  | 'invalid'
  | 'unknown';

export type SessionTerminalSource =
  | 'initial_entry'
  | 'initial_fetch'
  | 'poll'
  | 'countdown'
  | 'extend'
  | 'manual_end';

export interface SessionTerminalContext {
  version: 1;
  eventId: string;
  reason: SessionTerminalReason;
  source: SessionTerminalSource;
  httpStatus: number | null;
  serverStatus: string | null;
  sessionId: number | null;
  occurredAt: string;
}

export type CreateSessionTerminalContextInput = Omit<
  SessionTerminalContext,
  'version' | 'eventId' | 'occurredAt'
>;

interface SessionResourceTerminalFields {
  id: number;
  status: string;
  is_active: boolean;
  is_expired: boolean;
}

const SERVER_STATUS_PATTERN = /^[A-Z0-9_.:-]{1,64}$/i;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const TERMINAL_REASONS = new Set<SessionTerminalReason>([
  'completed',
  'expired',
  'unauthorized',
  'not_found',
  'invalid',
  'unknown',
]);

const TERMINAL_SOURCES = new Set<SessionTerminalSource>([
  'initial_entry',
  'initial_fetch',
  'poll',
  'countdown',
  'extend',
  'manual_end',
]);

export const SESSION_TERMINAL_CONTEXT_KEY = 'olulo_table_session_terminal_v1';
export const SESSION_TERMINAL_TTL_MS = 30 * 60 * 1000;

function normalizeServerStatus(value: unknown): string | null {
  return typeof value === 'string' && SERVER_STATUS_PATTERN.test(value) ? value : null;
}

export function createSessionTerminalContext(
  input: CreateSessionTerminalContextInput,
): SessionTerminalContext {
  return {
    version: 1,
    eventId: crypto.randomUUID(),
    reason: input.reason,
    source: input.source,
    httpStatus: input.httpStatus,
    serverStatus: input.serverStatus,
    sessionId: input.sessionId,
    occurredAt: new Date().toISOString(),
  };
}

export function createInitialEntrySessionTerminal(): SessionTerminalContext {
  return createSessionTerminalContext({
    reason: 'invalid',
    source: 'initial_entry',
    httpStatus: null,
    serverStatus: null,
    sessionId: null,
  });
}

export function getSessionTerminalReference(eventId: string): string {
  return eventId.slice(0, 8).toUpperCase();
}

export function mapSessionResourceToTerminal(
  resource: SessionResourceTerminalFields,
  source: SessionTerminalSource,
): SessionTerminalContext | null {
  let reason: SessionTerminalReason | null = null;

  if (resource.status === 'completed') {
    reason = 'completed';
  } else if (resource.status === 'expired' || resource.is_expired) {
    reason = 'expired';
  } else if (resource.status === 'cancelled') {
    reason = 'invalid';
  } else if (resource.status === 'active' && !resource.is_active) {
    reason = 'unknown';
  } else if (resource.status !== 'active' && !resource.is_active) {
    reason = 'unknown';
  }

  if (!reason) return null;

  return createSessionTerminalContext({
    reason,
    source,
    httpStatus: null,
    serverStatus: normalizeServerStatus(resource.status),
    sessionId: resource.id,
  });
}

function isApiErrorLike(error: unknown): error is Error & {
  status: number;
  code?: unknown;
} {
  if (!(error instanceof Error)) return false;

  const candidate = error as Error & { status?: unknown };
  return typeof candidate.status === 'number' && Number.isFinite(candidate.status);
}

export function mapApiErrorToTerminal(
  error: unknown,
  source: SessionTerminalSource,
): SessionTerminalContext | null {
  if (!isApiErrorLike(error)) return null;

  const reasonByStatus: Partial<Record<number, SessionTerminalReason>> = {
    401: 'unauthorized',
    404: 'not_found',
    410: 'invalid',
  };
  const reason = reasonByStatus[error.status];
  if (!reason) return null;

  return createSessionTerminalContext({
    reason,
    source,
    httpStatus: error.status,
    serverStatus: normalizeServerStatus(error.code),
    sessionId: null,
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isNullableNonNegativeInteger(value: unknown): value is number | null {
  return value === null || (Number.isInteger(value) && (value as number) >= 0);
}

function isCanonicalIsoDate(value: unknown): value is string {
  if (typeof value !== 'string') return false;

  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && new Date(timestamp).toISOString() === value;
}

export function validateSessionTerminalContext(
  value: unknown,
  now: number = Date.now(),
): SessionTerminalContext | null {
  if (!isRecord(value) || !Number.isFinite(now)) return null;

  const {
    version,
    eventId,
    reason,
    source,
    httpStatus,
    serverStatus,
    sessionId,
    occurredAt,
  } = value;
  const validatedServerStatus =
    serverStatus === null ? null : normalizeServerStatus(serverStatus);

  if (
    version !== 1 ||
    typeof eventId !== 'string' ||
    !UUID_PATTERN.test(eventId) ||
    typeof reason !== 'string' ||
    !TERMINAL_REASONS.has(reason as SessionTerminalReason) ||
    typeof source !== 'string' ||
    !TERMINAL_SOURCES.has(source as SessionTerminalSource) ||
    !isNullableNonNegativeInteger(httpStatus) ||
    !(serverStatus === null || validatedServerStatus !== null) ||
    !isNullableNonNegativeInteger(sessionId) ||
    !isCanonicalIsoDate(occurredAt)
  ) {
    return null;
  }

  const age = now - Date.parse(occurredAt);
  if (age < 0 || age >= SESSION_TERMINAL_TTL_MS) return null;

  return {
    version: 1,
    eventId,
    reason: reason as SessionTerminalReason,
    source: source as SessionTerminalSource,
    httpStatus,
    serverStatus: validatedServerStatus,
    sessionId,
    occurredAt,
  };
}

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function removeStoredContext(storage: Storage): void {
  try {
    storage.removeItem(SESSION_TERMINAL_CONTEXT_KEY);
  } catch {
    // Storage access can be denied independently of the session lifecycle.
  }
}

export function persistSessionTerminalContext(context: SessionTerminalContext): boolean {
  const validated = validateSessionTerminalContext(context);
  if (!validated) return false;

  const storage = getSessionStorage();
  if (!storage) return false;

  try {
    storage.setItem(SESSION_TERMINAL_CONTEXT_KEY, JSON.stringify(validated));
    return true;
  } catch {
    return false;
  }
}

export function readSessionTerminalContext(
  now: number = Date.now(),
): SessionTerminalContext | null {
  const storage = getSessionStorage();
  if (!storage) return null;

  try {
    const stored = storage.getItem(SESSION_TERMINAL_CONTEXT_KEY);
    if (!stored) return null;

    const validated = validateSessionTerminalContext(JSON.parse(stored), now);
    if (validated) return validated;

    removeStoredContext(storage);
    return null;
  } catch {
    removeStoredContext(storage);
    return null;
  }
}

export function clearSessionTerminalContext(): void {
  const storage = getSessionStorage();
  if (!storage) return;

  removeStoredContext(storage);
}
